/**
 * HeadDepositModal Component
 * 본사 입금신청 모달 - 입금 금액 입력 및 계좌 정보 표시
 */
import React, { useState, useCallback, useMemo } from 'react';
import { HeadDepositModalProps } from '../types/headWithdraw.types';
import { ToastType } from "@/shared/components/feedback/Toast/Toast.types";
import { useToast } from "@/shared/components/feedback/Toast/ToastContext";
import { formatAmountInput, formatAmount, parseAmountString } from '../lib/amountFormatterHelpers';
import AccountInfoSection from './AccountInfoSection';
import { AMOUNT_INPUT_STYLE } from './depositModal.helpers';
import '../styles/WithdrawModal.css';

const HeadDepositModal: React.FC<HeadDepositModalProps> = ({
    withdrawableAmount,
    depositFee = 300, // 기본값 300원
    accountInfo,
    onClose,
    onRequest
}) => {
    // ======== Hooks ========
    const { showToast } = useToast();

    // ======== State ========
    const [amount, setAmount] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // ======== Derived Values ========
    /** 입력된 금액의 숫자 값 */
    const numericAmount = useMemo(() => parseAmountString(amount), [amount]);

    // ======== Event Handlers ========
    /** 모달 배경 클릭 시 닫기 */
    const handleBackdropClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) onClose();
    }, [onClose]);

    /** 금액 입력 변경 - 숫자만 추출하여 천단위 콤마 포맷 적용 */
    const handleAmountChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setAmount(formatAmountInput(e.target.value));
    }, []);

    /** 입금 신청 제출 - 유효성 검사 후 요청 */
    const handleSubmit = useCallback(async () => {
        if (numericAmount <= 0) {
            showToast('입금 금액을 입력해주세요.', { type: ToastType.INFO });
            return;
        }

        if (numericAmount <= depositFee) {
            showToast(`입금 금액은 수수료(${depositFee.toLocaleString()}원)보다 커야 합니다.`, { type: ToastType.INFO });
            return;
        }

        setIsSubmitting(true);
        try {
            await onRequest(numericAmount);
        } finally {
            setIsSubmitting(false);
        }
    }, [numericAmount, depositFee, onRequest, showToast]);

    // ======== Render ========
    return (
        <div className="modal" onClick={handleBackdropClick}>
            <div className="modal-content">
                {/* 헤더 */}
                <div className="modal-header">
                    <h2 className="modal-title">입금신청</h2>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>

                {/* 본문 */}
                <div className="modal-body">
                    {/* 금액 입력 섹션 */}
                    <div className="withdrawal-input-section">
                        <div className="input-group">
                            <input
                                type="text"
                                className="form-input"
                                placeholder="입금 금액을 입력하세요"
                                value={amount}
                                onChange={handleAmountChange}
                                style={AMOUNT_INPUT_STYLE}
                                disabled={isSubmitting}
                            />
                            <button
                                className="btn btn-blue"
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? '처리중...' : '입금신청'}
                            </button>
                        </div>
                    </div>

                    {/* 입금 요청 내역 섹션 */}
                    <div className="withdrawal-details">
                        <h3 className="section-title">입금요청내역</h3>
                        <div className="details-grid">
                            <div className="detail-row">
                                <span className="detail-label">신청금액:</span>
                                <span className="detail-value">{formatAmount(numericAmount)} 원</span>
                            </div>
                        </div>

                        {/* 계좌 정보 */}
                        <AccountInfoSection
                            accountInfo={accountInfo}
                            withdrawableAmount={withdrawableAmount}
                            depositFirst={true}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HeadDepositModal;
